import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  contractTypeListService,
  contractTypeDetailService,
  contractTypeCreateService,
  contractTypeUpdateService,
  contractTypeListShortService,
} from "./services";

type ContractTypeListResponse = Awaited<
  ReturnType<typeof contractTypeListService>
>;
type ContractTypeShortListResponse = Awaited<
  ReturnType<typeof contractTypeListShortService>
>;
type ContractTypeDetailResponse = Awaited<
  ReturnType<typeof contractTypeDetailService>
>;

export const useContractTypes = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof contractTypeListService>[0];
  options?: QueryObserverOptions<unknown, unknown, ContractTypeListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "contractTypeListHooks",
    queryFn: () => contractTypeListService(params),
    ...options,
  });

  return response as UseQueryResult<ContractTypeListResponse>;
};

export const useShortContractTypes = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof contractTypeListShortService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    ContractTypeShortListResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "contractTypeShortListHooks",
    queryFn: () => contractTypeListShortService(params),
    ...options,
  });

  return response as UseQueryResult<ContractTypeShortListResponse>;
};

export const useContractTypeDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof contractTypeDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, ContractTypeDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "contractTypeDetailHooks",
    queryFn: () => contractTypeDetailService(params),
    ...options,
  });

  return response as UseQueryResult<ContractTypeDetailResponse>;
};

export const useContractTypeUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    ContractTypeDetailResponse,
    unknown,
    Parameters<typeof contractTypeUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "contractTypeUpdateHooks",
    mutationFn: contractTypeUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useContractTypeCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    ContractTypeDetailResponse,
    unknown,
    Parameters<typeof contractTypeCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "contractTypeCreateHooks",
    mutationFn: contractTypeCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
